<script setup>
import { computed, onBeforeMount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';

import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import StepList from './StepList.vue';

const props = defineProps({
  boardId: { type: Number, default: null },
});

const getters = useStoreGetters();
const store = useStore();
const router = useRouter();
const route = useRoute();
const { t } = useI18n();

const isEdit = computed(() => Boolean(props.boardId));

const name = ref('');
const description = ref('');
const inboxIds = ref([]);
const agentIds = ref([]);
const autoCreateTask = ref(false);
const isSaving = ref(false);

const inboxes = computed(() => getters['inboxes/getInboxes'].value || []);
const agents = computed(() => getters['agents/getAgents'].value || []);
const board = computed(() =>
  isEdit.value ? getters['kanban/boards/getBoardById'].value(props.boardId) : null
);

const hydrate = b => {
  if (!b) return;
  name.value = b.name || '';
  description.value = b.description || '';
  inboxIds.value = [...(b.inbox_ids || [])];
  agentIds.value = [...(b.agent_ids || [])];
  autoCreateTask.value = Boolean(b.settings?.auto_create_task);
};

const toggleInbox = id => {
  const idx = inboxIds.value.indexOf(id);
  if (idx === -1) inboxIds.value.push(id);
  else inboxIds.value.splice(idx, 1);
};

const toggleAgent = id => {
  const idx = agentIds.value.indexOf(id);
  if (idx === -1) agentIds.value.push(id);
  else agentIds.value.splice(idx, 1);
};

const goBack = () => {
  router.push({ name: 'settings_kanban_index' });
};

const submit = async () => {
  if (!name.value.trim()) return;
  isSaving.value = true;
  const payload = {
    board: {
      name: name.value.trim(),
      description: description.value,
      settings: { auto_create_task: autoCreateTask.value },
    },
    inbox_ids: inboxIds.value,
    agent_ids: agentIds.value,
  };
  try {
    if (isEdit.value) {
      await store.dispatch('kanban/boards/update', { id: props.boardId, ...payload });
      useAlert(t('KANBAN.SETTINGS.SAVE.UPDATE_SUCCESS'));
    } else {
      const created = await store.dispatch('kanban/boards/create', payload);
      useAlert(t('KANBAN.SETTINGS.SAVE.CREATE_SUCCESS'));
      router.push({ name: 'settings_kanban_edit', params: { boardId: created.id } });
      return;
    }
  } catch (error) {
    useAlert(t('KANBAN.SETTINGS.SAVE.ERROR'));
  } finally {
    isSaving.value = false;
  }
};

onBeforeMount(async () => {
  store.dispatch('agents/get');
  store.dispatch('inboxes/get');
  if (isEdit.value) {
    await store.dispatch('kanban/boards/fetchOne', props.boardId);
    hydrate(board.value);
  }
});

watch(board, b => hydrate(b));
watch(() => route.params.boardId, () => {
  if (!isEdit.value) {
    name.value = '';
    description.value = '';
    inboxIds.value = [];
    agentIds.value = [];
    autoCreateTask.value = false;
  }
});
</script>

<template>
  <SettingsLayout>
    <template #header>
      <BaseSettingsHeader
        :title="
          isEdit ? $t('KANBAN.SETTINGS.EDIT_TITLE') : $t('KANBAN.SETTINGS.NEW_TITLE')
        "
        :description="$t('KANBAN.SETTINGS.FORM_DESCRIPTION')"
        :back-button-label="$t('KANBAN.SETTINGS.BACK')"
      />
    </template>
    <template #body>
      <form class="flex flex-col gap-6 w-full" @submit.prevent="submit">
        <woot-input
          v-model="name"
          class="w-full"
          :label="$t('KANBAN.SETTINGS.FORM.NAME.LABEL')"
          :placeholder="$t('KANBAN.SETTINGS.FORM.NAME.PLACEHOLDER')"
        />
        <woot-input
          v-model="description"
          class="w-full"
          :label="$t('KANBAN.SETTINGS.FORM.DESCRIPTION.LABEL')"
          :placeholder="$t('KANBAN.SETTINGS.FORM.DESCRIPTION.PLACEHOLDER')"
        />

        <div>
          <label class="block mb-2 text-sm font-medium text-n-slate-12">
            {{ $t('KANBAN.SETTINGS.FORM.INBOXES.LABEL') }}
          </label>
          <div class="flex flex-col gap-2 max-h-48 overflow-auto p-3 border border-n-weak rounded-md">
            <div
              v-for="inbox in inboxes"
              :key="inbox.id"
              class="flex items-center gap-2"
            >
              <Checkbox
                :model-value="inboxIds.includes(inbox.id)"
                @change="() => toggleInbox(inbox.id)"
              />
              <span class="text-body-main text-n-slate-12">{{ inbox.name }}</span>
            </div>
            <p v-if="!inboxes.length" class="text-body-small text-n-slate-11">
              {{ $t('KANBAN.SETTINGS.FORM.INBOXES.EMPTY') }}
            </p>
          </div>
        </div>

        <div>
          <label class="block mb-2 text-sm font-medium text-n-slate-12">
            {{ $t('KANBAN.SETTINGS.FORM.AGENTS.LABEL') }}
          </label>
          <div class="flex flex-col gap-2 max-h-48 overflow-auto p-3 border border-n-weak rounded-md">
            <div
              v-for="agent in agents"
              :key="agent.id"
              class="flex items-center gap-2"
            >
              <Checkbox
                :model-value="agentIds.includes(agent.id)"
                @change="() => toggleAgent(agent.id)"
              />
              <span class="text-body-main text-n-slate-12">
                {{ agent.name }}
              </span>
            </div>
            <p v-if="!agents.length" class="text-body-small text-n-slate-11">
              {{ $t('KANBAN.SETTINGS.FORM.AGENTS.EMPTY') }}
            </p>
          </div>
        </div>

        <div class="flex items-center gap-2">
          <Checkbox v-model="autoCreateTask" />
          <label class="text-body-main text-n-slate-12">
            {{ $t('KANBAN.SETTINGS.FORM.AUTO_CREATE.LABEL') }}
          </label>
        </div>

        <div class="flex items-center justify-end gap-2">
          <Button
            faded
            slate
            type="button"
            :label="$t('KANBAN.SETTINGS.FORM.CANCEL')"
            @click="goBack"
          />
          <Button
            type="submit"
            :label="
              isEdit
                ? $t('KANBAN.SETTINGS.FORM.SAVE')
                : $t('KANBAN.SETTINGS.FORM.CREATE')
            "
            :disabled="!name.trim() || isSaving"
            :is-loading="isSaving"
          />
        </div>
      </form>

      <StepList
        v-if="isEdit && board"
        :board="board"
        class="mt-10"
      />
    </template>
  </SettingsLayout>
</template>
